import React from 'react'
import { COLORS } from '../theme'

// Work-type mix: feature / support / bug / tech debt / unclear.
//
// This panel shows an LLM CLASSIFICATION, not a recorded field, and it is drawn
// to keep that distinction alive rather than let it decay into a fact:
//
//  - `unclear` gets a real slice, in a deliberately un-chartlike grey, and is
//    never dropped or redistributed. It is ~22% of the corpus; hiding it would
//    make the other four appear to account for everything, and would inflate
//    every percentage by about a quarter.
//  - Percentages are of the WHOLE corpus including unclear. A denominator that
//    quietly excluded the unknowns is the standard way a mix like this starts
//    overstating itself.
//  - An "inferred" chip sits on the panel. Two dashboards down, nobody
//    remembers which numbers were measured and which were judged.

const ORDER = ['feature', 'bug', 'tech_debt', 'support', 'unclear']
const META = {
    feature: { label: 'Feature', color: '#3b82f6' },
    bug: { label: 'Bug', color: '#ef4444' },
    tech_debt: { label: 'Tech debt', color: '#f59e0b' },
    support: { label: 'Support', color: '#8b5cf6' },
    // Deliberately the muted border colour, not a fifth bright hue: it should
    // read as an absence of an answer, not as a fifth kind of work.
    unclear: { label: 'Unclear', color: COLORS.border },
}

export default function MixPanel({ rows, note }) {
    if (!rows || rows.length === 0) return null
    const byType = Object.fromEntries(rows.map((r) => [r.type, r.count || 0]))
    const total = ORDER.reduce((n, t) => n + (byType[t] || 0), 0)
    if (total === 0) return null
    const pct = (n) => Math.round((n / total) * 100)
    const present = ORDER.filter((t) => (byType[t] || 0) > 0)

    return (
        <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 30, fontWeight: 700, color: COLORS.text, lineHeight: 1 }}>
                    {pct(byType.feature || 0)}%
                </span>
                <span style={{ fontSize: 12.5, color: COLORS.textMuted }}>feature work</span>
                <div style={{ flex: 1 }} />
                <span
                    style={{
                        fontSize: 10,
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        color: COLORS.accent,
                        border: `1px solid ${COLORS.accent}66`,
                        borderRadius: 3,
                        padding: '2px 7px',
                        whiteSpace: 'nowrap',
                    }}
                    title="Classified by an LLM from each epic's title and description — nothing in Jira records work type."
                >
                    inferred
                </span>
            </div>

            <div
                style={{ display: 'flex', height: 16, borderRadius: 3, overflow: 'hidden', width: '100%' }}
                role="img"
                aria-label={present.map((t) => `${META[t].label} ${pct(byType[t])}%`).join(', ')}
            >
                {present.map((t) => (
                    <span
                        key={t}
                        style={{ flex: byType[t], background: META[t].color }}
                        title={`${META[t].label}: ${byType[t]} epics (${pct(byType[t])}%)`}
                    />
                ))}
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))',
                    gap: 8,
                    marginTop: 12,
                }}
            >
                {ORDER.map((t) => (
                    <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                background: META[t].color,
                                borderRadius: 2,
                                flexShrink: 0,
                            }}
                        />
                        <span style={{ fontSize: 12, color: COLORS.textMuted, whiteSpace: 'nowrap' }}>
                            {META[t].label}
                        </span>
                        <span
                            style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: COLORS.text,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {byType[t] || 0}
                        </span>
                        <span style={{ fontSize: 11, color: COLORS.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                            {pct(byType[t] || 0)}%
                        </span>
                    </div>
                ))}
            </div>

            {note && (
                <p
                    style={{
                        fontSize: 11.5,
                        color: COLORS.textMuted,
                        lineHeight: 1.55,
                        margin: '12px 0 0',
                        paddingTop: 8,
                        borderTop: `1px dashed ${COLORS.border}`,
                    }}
                >
                    {note}
                </p>
            )}
        </div>
    )
}
